import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import {
  useContactBillingSummary,
  useContactBillingActivity,
  type BillingInvoice,
  type BillingActivityEvent,
} from './hooks/useContactBilling';

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATUS_VARIANTS: Record<string, 'default' | 'warning' | 'success' | 'danger' | 'info'> = {
  draft: 'default',
  sent: 'info',
  viewed: 'info',
  partially_paid: 'warning',
  paid: 'success',
  overdue: 'danger',
  voided: 'default',
  void: 'default',
  accepted: 'success',
  declined: 'danger',
  expired: 'danger',
  issued: 'info',
  applied: 'success',
  active: 'success',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  bank_transfer: 'Bank Transfer',
  cheque: 'Cheque',
  credit_card: 'Credit Card',
  cash: 'Cash',
  other: 'Other',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
}
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

type SectionId =
  | 'invoices'
  | 'quotes'
  | 'payments'
  | 'credit_notes'
  | 'debit_notes'
  | 'write_offs'
  | 'discounts'
  | 'activity';

// ─── Main Component ─────────────────────────────────────────────────────────

interface ContactBillingTabProps {
  contactId: string;
}

export function ContactBillingTab({ contactId }: ContactBillingTabProps) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [invoicePage, setInvoicePage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [activeSection, setActiveSection] = useState<SectionId>('invoices');

  const { data: summary, isLoading } = useContactBillingSummary(contactId, {
    page: invoicePage,
    pageSize: 10,
    status: statusFilter,
  });
  const { data: activityData } = useContactBillingActivity(contactId, 1, 30);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
        <span className="ml-2 text-sm text-gray-500">Loading billing data...</span>
      </div>
    );
  }

  const stats = summary?.stats;

  // Build visible sections based on permissions
  const sections: { id: SectionId; label: string; count?: number }[] = [];
  if (can(PERMISSIONS.BILLING_READ))
    sections.push({ id: 'invoices', label: 'Invoices', count: stats?.totalInvoices });
  if (can(PERMISSIONS.BILLING_MANAGE_QUOTES))
    sections.push({ id: 'quotes', label: 'Quotes', count: summary?.quotes?.length });
  if (can(PERMISSIONS.BILLING_RECORD_PAYMENT))
    sections.push({ id: 'payments', label: 'Payments', count: summary?.payments?.length });
  if (can(PERMISSIONS.BILLING_CREATE_CREDIT_NOTE))
    sections.push({
      id: 'credit_notes',
      label: 'Credit Notes',
      count: summary?.creditNotes?.length,
    });
  if (can(PERMISSIONS.BILLING_CREATE_DEBIT_NOTE))
    sections.push({ id: 'debit_notes', label: 'Debit Notes', count: summary?.debitNotes?.length });
  if (can(PERMISSIONS.BILLING_CREATE_WRITE_OFF))
    sections.push({ id: 'write_offs', label: 'Write-Offs', count: summary?.writeOffs?.length });
  sections.push({ id: 'activity', label: 'Activity', count: activityData?.pagination.total });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total Billed" value={formatCurrency(stats.totalBilled)} />
          <StatCard label="Total Paid" value={formatCurrency(stats.totalPaid)} variant="success" />
          <StatCard
            label="Outstanding"
            value={formatCurrency(stats.totalOutstanding)}
            variant="warning"
          />
          <StatCard label="Invoices" value={String(stats.totalInvoices)} />
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-border dark:border-border-dark pb-0">
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveSection(s.id)}
            className={`whitespace-nowrap px-3 py-2 text-xs font-medium border-b-2 transition-colors ${activeSection === s.id ? 'tab-active' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
          >
            {s.label}
            {s.count !== undefined && s.count > 0 && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {s.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Invoices */}
      {activeSection === 'invoices' && (
        <Card>
          <CardHeader
            title="Invoices"
            action={
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setInvoicePage(1);
                }}
                className="rounded-md border border-border bg-white px-2 py-1 text-xs dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="partially_paid">Partially Paid</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="voided">Voided</option>
              </select>
            }
          />
          <CardContent>
            <TransactionList
              items={(summary?.invoices ?? []).map((inv: BillingInvoice) => ({
                id: inv.id,
                type: 'invoice' as const,
                number: inv.invoice_number,
                amount: inv.total_receivable || inv.total,
                balance: inv.outstanding_balance,
                status: inv.status,
                date: inv.issue_date,
              }))}
              onView={(id) => navigate(`/billing/${id}?type=invoice`)}
            />
            {summary?.pagination && summary.pagination.totalPages > 1 && (
              <Pagination
                page={invoicePage}
                totalPages={summary.pagination.totalPages}
                onChange={setInvoicePage}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Quotes */}
      {activeSection === 'quotes' && (
        <Card>
          <CardHeader title="Quotes" />
          <CardContent>
            <TransactionList
              items={(summary?.quotes ?? []).map((q) => ({
                id: q.id,
                type: 'quote' as const,
                number: q.quote_number,
                amount: q.total,
                balance: q.total,
                status: q.status,
                date: q.created_at,
              }))}
              onView={(id) => navigate(`/billing/${id}?type=quote`)}
            />
          </CardContent>
        </Card>
      )}

      {/* Payments */}
      {activeSection === 'payments' && (
        <Card>
          <CardHeader title="Payments" />
          <CardContent>
            {(summary?.payments ?? []).length > 0 ? (
              <div className="divide-y divide-border dark:divide-border-dark">
                {summary!.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(p.amount)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method} ·{' '}
                        {formatDate(p.payment_date)}
                        {p.reference_number ? ` · Ref: ${p.reference_number}` : ''}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">{p.invoice_number}</span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState message="No payments recorded." />
            )}
          </CardContent>
        </Card>
      )}

      {/* Credit Notes */}
      {activeSection === 'credit_notes' && (
        <Card>
          <CardHeader title="Credit Notes" />
          <CardContent>
            <TransactionList
              items={(summary?.creditNotes ?? []).map((cn) => ({
                id: cn.id,
                type: 'credit_note' as const,
                number: cn.credit_note_number,
                amount: cn.amount,
                balance: 0,
                status: cn.status,
                date: cn.created_at,
              }))}
              onView={(id) => navigate(`/billing/${id}?type=credit_note`)}
            />
          </CardContent>
        </Card>
      )}

      {/* Debit Notes */}
      {activeSection === 'debit_notes' && (
        <Card>
          <CardHeader title="Debit Notes" />
          <CardContent>
            <TransactionList
              items={(summary?.debitNotes ?? []).map((dn) => ({
                id: dn.id,
                type: 'debit_note' as const,
                number: dn.debit_note_number || `DN-${dn.id.slice(0, 8)}`,
                amount: dn.amount,
                balance: dn.amount,
                status: dn.status,
                date: dn.issued_at || dn.created_at,
              }))}
              onView={(id) => navigate(`/billing/${id}?type=debit_note`)}
            />
          </CardContent>
        </Card>
      )}

      {/* Write-Offs */}
      {activeSection === 'write_offs' && (
        <Card>
          <CardHeader title="Write-Offs" />
          <CardContent>
            <TransactionList
              items={(summary?.writeOffs ?? []).map((wo) => ({
                id: wo.id,
                type: 'write_off' as const,
                number: wo.write_off_number,
                amount: wo.amount,
                balance: 0,
                status: wo.status,
                date: wo.transaction_date,
              }))}
              onView={(id) => navigate(`/billing/${id}?type=write_off`)}
            />
          </CardContent>
        </Card>
      )}

      {/* Discounts */}
      {activeSection === 'discounts' && (
        <Card>
          <CardHeader title="Discounts" />
          <CardContent>
            <TransactionList
              items={(summary?.discounts ?? []).map((d) => ({
                id: d.id,
                type: 'discount' as const,
                number: d.discount_number,
                amount: d.amount,
                balance: 0,
                status: d.status,
                date: d.transaction_date,
              }))}
              onView={(id) => navigate(`/billing/${id}?type=discount`)}
            />
          </CardContent>
        </Card>
      )}

      {/* Activity Log */}
      {activeSection === 'activity' && (
        <Card>
          <CardHeader title="Billing Activity" />
          <CardContent>
            {activityData?.activities && activityData.activities.length > 0 ? (
              <div className="relative">
                <div className="absolute left-3 top-2 bottom-2 w-px bg-gray-200 dark:bg-gray-700" />
                <div className="space-y-4">
                  {activityData.activities.map((event: BillingActivityEvent, idx: number) => (
                    <div key={`${event.reference_id}-${idx}`} className="relative flex gap-3 pl-1">
                      <div
                        className={`relative z-10 mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ring-2 ring-white dark:ring-gray-900 ${event.event_type === 'payment_received' ? 'bg-green-500' : 'bg-brand-500'}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                          {event.description}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatDate(event.event_date)}
                          {event.payment_method
                            ? ` · ${PAYMENT_METHOD_LABELS[event.payment_method] || event.payment_method}`
                            : ''}
                        </p>
                      </div>
                      <span className="flex-shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {formatCurrency(event.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState message="No billing activity yet." />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  variant,
}: {
  label: string;
  value: string;
  variant?: 'success' | 'warning' | 'danger';
}) {
  const colorClasses =
    variant === 'success'
      ? 'text-green-700 dark:text-green-400'
      : variant === 'warning'
        ? 'text-amber-700 dark:text-amber-400'
        : variant === 'danger'
          ? 'text-red-700 dark:text-red-400'
          : 'text-gray-900 dark:text-gray-100';
  return (
    <div className="rounded-lg border border-border bg-white p-4 dark:border-border-dark dark:bg-surface-dark-secondary">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${colorClasses}`}>{value}</p>
    </div>
  );
}

interface TransactionItem {
  id: string;
  type: string;
  number: string;
  amount: number;
  balance: number;
  status: string;
  date: string;
}

function TransactionList({
  items,
  onView,
}: {
  items: TransactionItem[];
  onView: (id: string) => void;
}) {
  if (items.length === 0) return <EmptyState message="No records found." />;
  return (
    <div className="divide-y divide-border dark:divide-border-dark">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/30 -mx-4 px-4 transition-colors"
          onClick={() => onView(item.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onView(item.id)}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.number}</p>
              <p className="text-xs text-gray-500">{formatDate(item.date)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Badge variant={STATUS_VARIANTS[item.status] || 'default'} size="sm">
              {item.status.replace(/_/g, ' ')}
            </Badge>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 w-24 text-right">
              {formatCurrency(item.amount)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between border-t border-border pt-3 dark:border-border-dark">
      <p className="text-xs text-gray-500">
        Page {page} of {totalPages}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="rounded border border-border px-2 py-1 text-xs disabled:opacity-50 dark:border-border-dark"
        >
          Prev
        </button>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="rounded border border-border px-2 py-1 text-xs disabled:opacity-50 dark:border-border-dark"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">{message}</p>;
}
